import Button from "@/components/Button/button";
import Divider from "@/components/Divider/divider";
import FormNewsletter from "@/components/FormNewsletter/form-newsletter";
import Main from "@/components/Layout/main";
import TextBlock from "@/components/TextBlock/text-block";
import { bpInRange, BreakpointContext } from "@/context/BreakpointContext";
import { PageContext } from "@/context/PageContext";
import { GetBlogStories } from "@/lib/storyblok-gapi";
import {
  BlogIndexStoryblok,
  RichtextStoryblok,
} from "@/types/component-types-sb";
import classnames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import { formatDate } from "../../helpers/utils";
import BlogIndexFeaturedItem from "./blog-index-featured-item";
import BlogIndexItem from "./blog-index-item";
import styles from "./blog.module.scss";

interface Props {
  content: BlogIndexStoryblok;
  stories: GetBlogStories["BlogItems"]["items"];
}

const BlogIndex = (props: Props) => {
  const { data, setData } = useContext(PageContext);

  useEffect(() => {
    setData({ ...data, page_class: "blog" });
    return () => setData({ ...data, page_class: "" });
  }, []);

  const { currentBp } = useContext(BreakpointContext);
  const smallViewport = bpInRange({ currentBp, bp: "m", range: "down" });
  const [trancheSize, setTrancheSize] = useState(0);
  const gridRef = useRef(null);

  useEffect(() => {
    setTrancheSize(smallViewport ? 6 : 9);
  }, [smallViewport]);

  const featuredStory = props.content.featured_story as NonNullable<
    Exclude<BlogIndexStoryblok["featured_story"], string>
  >;

  const {
    id,
    name,
    content: { intro: featuredIntro, article_image } = {
      intro: "check featured story is defined and published",
      article_image: {
        id: -1,
        name: "",
        filename: "",
      },
    },
    full_slug,
    first_published_at,
  } = featuredStory;

  const { stories } = props;

  const classes = classnames(styles.blogIndex);

  const loadMore = () => setTrancheSize(trancheSize + (smallViewport ? 8 : 9));

  // filter featured story out of stories
  const filteredStories = stories.filter((story) => story.id !== String(id));

  return (
    <Main className={classes}>
      {smallViewport ? (
        <BlogIndexItem
          featured
          name={name}
          content={{ article_image }}
          intro={featuredIntro}
          full_slug={full_slug}
          published={formatDate(first_published_at || "")}
        />
      ) : (
        <BlogIndexFeaturedItem image={article_image} link={`/${full_slug}`}>
          <IndexFeaturedItemCopy
            title={name}
            intro={featuredIntro}
            slug={`/${full_slug}`}
            published={formatDate(first_published_at || "")}
          />
        </BlogIndexFeaturedItem>
      )}
      <h2 className={"ef-h5 " + styles.indexTitle}>Previous stories</h2>
      <Divider className={styles.indexDivider} />
      <div className={styles.indexContentGrid} ref={gridRef}>
        {filteredStories.map(
          (story, i) =>
            i < trancheSize && (
              <BlogIndexItem
                key={story.id}
                {...story}
                published={formatDate(story?.first_published_at)}
              />
            )
        )}
      </div>
      {filteredStories.length > trancheSize && (
        <div className={styles.loadMore}>
          <Divider className={styles.loadMoreDivider} />
          <Button icon="IconChevronDown" onClick={loadMore}>
            Load More
          </Button>
          <Divider className={styles.loadMoreDivider} />
        </div>
      )}
      <FormNewsletter />
    </Main>
  );
};

export const IndexFeaturedItemCopy = (props: {
  title: string;
  intro?: RichtextStoryblok | string;
  slug: string;
  published: string;
}) => {
  const { title, intro, published } = props;
  return (
    <div>
      <p className="ef-text-caption u-mb-s">{published}</p>
      <h1 className="ef-h4">{title}</h1>
      {intro && (
        <TextBlock
          max_lines={"4"}
          content={intro as any}
          _uid=""
          component="text-block"
        />
      )}
    </div>
  );
};

export default BlogIndex;
